import React, { useState, useEffect, useRef } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Dot } from 'recharts';

const DynamicChart = ({ data }) => {
    const [displayData, setDisplayData] = useState([]);
    const prevDataLength = useRef(0);
    const animationTimeoutRef = useRef(null);

    useEffect(() => {
        // Só exibe o gráfico se houver pelo menos saldo inicial + 1 operação
        if (!data || data.length < 2) {
            setDisplayData([]);
            return;
        }

        // Se há novos dados, anima a entrada
        if (data.length > prevDataLength.current) {
            if (animationTimeoutRef.current) {
                clearTimeout(animationTimeoutRef.current);
            }
            animationTimeoutRef.current = setTimeout(() => {
                const chartData = data.map((value, idx) => ({
                    round: idx + 1,
                    saldo: Number(value) || 0,
                    isNew: idx === data.length - 1
                }));
                setDisplayData(chartData);
            }, 50);
            prevDataLength.current = data.length;
        } else {
            const chartData = data.map((value, idx) => ({
                round: idx + 1,
                saldo: Number(value) || 0,
                isNew: false
            }));
            setDisplayData(chartData);
        }
        return () => {
            if (animationTimeoutRef.current) {
                clearTimeout(animationTimeoutRef.current);
            }
        };
    }, [data]);

    // Tooltip customizado para mostrar o saldo formatado
    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const saldo = payload[0].value;
            const round = payload[0].payload.round;

            return (
                <div style={{
                    background: '#232b36',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: '1px solid #00C689',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                }}>
                    <p style={{ margin: 0, color: '#00C689', fontSize: '11px', fontWeight: 600 }}>
                        Operação {round}
                    </p>
                    <p style={{ margin: '4px 0 0 0', color: '#fff', fontSize: '13px', fontWeight: 700 }}>
                        ${saldo.toFixed(2)}
                    </p>
                </div>
            );
        }
        return null;
    };

    // Dot customizado para destacar pontos de WIN/LOSS
    const CustomDot = (props) => {
        const { cx, cy, payload, index } = props;

        if (index === 0) {
            // Primeiro ponto (saldo inicial) - azul
            return (
                <circle
                    cx={cx}
                    cy={cy}
                    r={4}
                    fill="#00C689"
                    stroke="#00C689"
                    strokeWidth={2}
                />
            );
        }

        if (!displayData || displayData.length < 2) return null;

        const current = displayData[index].saldo;
        const previous = displayData[index - 1].saldo;

        let fillColor = '#00C689';
        let strokeColor = '#00C689';
        let radius = 4;

        if (current > previous) {
            // WIN - Verde brilhante
            fillColor = '#00ff15';
            strokeColor = '#00ff15';
            radius = payload.isNew ? 6 : 4;
        } else if (current < previous) {
            // LOSS - Vermelho
            fillColor = '#ff4444';
            strokeColor = '#ff4444';
            radius = payload.isNew ? 6 : 4;
        }

        return (
            <circle
                cx={cx}
                cy={cy}
                r={radius}
                fill={fillColor}
                stroke={strokeColor}
                strokeWidth={2}
                opacity={payload.isNew ? 1 : 0.9}
            />
        );
    };

    if (!displayData || displayData.length === 0) {
        return (
            <div style={{
                width: '100%',
                height: 220,
                minWidth: 220,
                minHeight: 160,
                background: '#10141a',
                borderRadius: 12,
                border: '1.5px solid #00C689',
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                marginBottom: 18,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#c0d0e6',
                fontSize: 13
            }}>
                Aguardando dados...
            </div>
        );
    }

    return (
        <div style={{
            width: '100%',
            height: 220,
            minWidth: 220,
            minHeight: 160,
            background: '#10141a',
            borderRadius: 12,
            border: '1.5px solid #00C689',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
            marginBottom: 18,
            overflow: 'hidden'
        }}>
            <ResponsiveContainer width="100%" height="100%">
                <LineChart
                    data={displayData}
                    margin={{ top: 20, right: 20, left: 10, bottom: 5 }}
                >
                    <CartesianGrid stroke="#233040" strokeDasharray="3 3" />
                    <XAxis
                        dataKey="round"
                        tick={{ fill: '#c0d0e6', fontSize: 11 }}
                        axisLine={false}
                        tickLine={false}
                        label={{ value: 'Operações', position: 'insideBottom', offset: -5, fill: '#c0d0e6', fontSize: 10 }}
                    />
                    <YAxis
                        tick={{ fill: '#c0d0e6', fontSize: 11 }}
                        axisLine={false}
                        tickLine={false}
                        domain={['auto', 'auto']}
                        tickFormatter={(value) => `$${value.toFixed(0)}`}
                    />
                    <Tooltip content={<CustomTooltip />} cursor={{ stroke: '#00C689', strokeWidth: 1, strokeDasharray: '3 3' }} />
                    <Line
                        type="monotone"
                        dataKey="saldo"
                        stroke="#00C689"
                        strokeWidth={2.5}
                        dot={<CustomDot />}
                        activeDot={{ r: 6, fill: '#00C689', stroke: '#00C689', strokeWidth: 3 }}
                        isAnimationActive={true}
                        animationDuration={800}
                        animationEasing="ease-out"
                    />
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
};

export default DynamicChart;